import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { apiClient } from '../../api/client';
import { KeyRound, Loader2, CheckCircle2, AlertTriangle, HelpCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const TIERS = {
  critical: { label: 'Critical', color: '#DC2626', bg: '#FEF2F2', desc: 'Moves money or grants full control — rotate every 90 days' },
  high: { label: 'High', color: '#EA580C', bg: '#FFF7ED', desc: 'User communications & identity — rotate every 180 days' },
  medium: { label: 'Medium', color: '#CA8A04', bg: '#FEFCE8', desc: 'Feature keys — rotate yearly' },
  low: { label: 'Low', color: '#6B7280', bg: '#F9FAFB', desc: 'Internal tokens & alerts — rotate yearly' },
};

const STATUS = {
  ok: { label: 'OK', color: '#059669', bg: '#ECFDF5', Icon: CheckCircle2 },
  due_soon: { label: 'Due soon', color: '#D97706', bg: '#FFFBEB', Icon: Clock },
  overdue: { label: 'Overdue', color: '#DC2626', bg: '#FEF2F2', Icon: AlertTriangle },
  unknown: { label: 'No date set', color: '#6B7280', bg: '#F3F4F6', Icon: HelpCircle },
};

const SecretRow = ({ item, onRotated }) => {
  const [saving, setSaving] = useState(false);
  const st = STATUS[item.status] || STATUS.unknown;
  const markRotated = async () => {
    setSaving(true);
    try {
      await apiClient.request(`/api/admin/secrets/${item.key}/rotated`, { method: 'POST', body: JSON.stringify({}) });
      toast.success(`${item.key} marked rotated today`);
      onRotated();
    } catch (e) {
      toast.error(e?.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };
  return (
    <tr data-testid={`secret-row-${item.key}`} className="border-t" style={{ borderColor: '#F0F0F0' }}>
      <td className="p-3">
        <p className="text-sm font-mono font-medium" style={{ color: '#1A1209' }}>{item.key}</p>
        <p className="text-[11px]" style={{ color: '#8A8580' }}>{item.service} · {item.purpose}</p>
      </td>
      <td className="p-3">
        {item.configured ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: '#059669' }}>● Set</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: '#9CA3AF' }}>○ Not set</span>
        )}
      </td>
      <td className="p-3 text-xs" style={{ color: '#44403A' }}>{item.last_rotated || '—'}</td>
      <td className="p-3 text-xs" style={{ color: '#44403A' }}>{item.due_date || '—'}</td>
      <td className="p-3">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
          style={{ color: st.color, backgroundColor: st.bg }}>
          <st.Icon size={11} /> {st.label}
        </span>
      </td>
      <td className="p-3">
        <button data-testid={`secret-rotated-btn-${item.key}`} onClick={markRotated} disabled={saving}
          className="px-2.5 py-1 rounded-lg text-[11px] font-medium disabled:opacity-50"
          style={{ backgroundColor: '#EEF2FF', color: '#4338CA' }}>
          {saving ? <Loader2 size={11} className="animate-spin inline" /> : 'Rotated today'}
        </button>
      </td>
    </tr>
  );
};

export default function AdminSecrets() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    apiClient.request('/api/admin/secrets/registry').then(setData).catch(e => setError(e?.message || 'Failed to load'));
  };
  useEffect(() => { load(); }, []);

  return (
    <AdminLayout pageTitle="API Keys & Secrets">
      <div className="space-y-6" data-testid="admin-secrets-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
              <KeyRound size={20} style={{ color: '#F5831F' }} /> Secrets Registry
            </h2>
            <p className="text-xs mt-1" style={{ color: '#8A8580' }}>
              Key names only — values are never displayed or stored here. Rotate keys at each provider's dashboard, then mark the date.
            </p>
          </div>
          {data && (
            <div className="flex gap-2">
              <span className="px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ backgroundColor: '#F5F5F4', color: '#44403A' }}>
                {data.total} keys tracked
              </span>
              <span data-testid="secrets-attention-count" className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                style={{ backgroundColor: data.needs_attention ? '#FEF2F2' : '#ECFDF5', color: data.needs_attention ? '#B91C1C' : '#059669' }}>
                {data.needs_attention} need attention
              </span>
            </div>
          )}
        </div>

        {error && <p className="text-sm" style={{ color: '#B91C1C' }}>{error}</p>}
        {!data && !error && <Loader2 className="animate-spin" style={{ color: '#F5831F' }} />}

        {data && Object.keys(TIERS).map(tier => {
          const tierItems = data.items.filter(i => i.tier === tier);
          if (!tierItems.length) return null;
          const t = TIERS[tier];
          return (
            <div key={tier} data-testid={`secrets-tier-${tier}`} className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E0E0E0' }}>
              <div className="px-4 py-3 flex items-center gap-3" style={{ backgroundColor: t.bg, borderBottom: `2px solid ${t.color}` }}>
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                <span className="text-sm font-bold" style={{ color: t.color }}>{t.label} — {tierItems.length} keys</span>
                <span className="text-[11px]" style={{ color: '#6B7280' }}>{t.desc}</span>
              </div>
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>
                    <th className="p-3 font-semibold">Key</th>
                    <th className="p-3 font-semibold">Configured</th>
                    <th className="p-3 font-semibold">Last rotated</th>
                    <th className="p-3 font-semibold">Rotation due</th>
                    <th className="p-3 font-semibold">Status</th>
                    <th className="p-3 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tierItems.map(item => <SecretRow key={item.key} item={item} onRotated={load} />)}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </AdminLayout>
  );
}
